/**
 * Web Share Target handler
 * Processes shared content from other apps
 * Parses BAC codes or dealer info and navigates appropriately
 */

/**
 * Parse shared text to extract BAC code
 * @param {string} text - Shared text
 * @returns {string|null} BAC code if found
 */
export function parseBacFromText(text) {
  if (!text) return null;

  // Look for BAC patterns: 6-8 digit numbers
  const bacPattern = /\b\d{6,8}\b/g;
  const matches = text.match(bacPattern);
  
  if (matches && matches.length > 0) {
    // Return first match (most likely BAC)
    return matches[0];
  }

  return null;
}

/**
 * Parse shared text to extract dealer name
 * @param {string} text - Shared text
 * @returns {string|null} Dealer name if found
 */
export function parseDealerNameFromText(text) {
  if (!text) return null;

  // Look for common dealer name patterns
  // This is a simple implementation - can be enhanced
  const lines = text.split('\n').filter(line => line.trim().length > 0);
  
  if (lines.length > 0) {
    // First non-empty line might be dealer name
    const firstLine = lines[0].trim();
    if (firstLine.length > 3 && firstLine.length < 100) {
      return firstLine;
    }
  }

  return null;
}

/**
 * Handle shared content from Web Share Target
 * @param {Object} sharedData - Shared data object
 * @param {Function} navigate - Navigation function (from react-router)
 * @returns {Object} Parsed data and navigation info
 */
export function handleShareTarget(sharedData, navigate) {
  const { text = '', title = '', url = '' } = sharedData;
  const combinedText = `${title} ${text} ${url}`.trim();

  const bac = parseBacFromText(combinedText);
  const dealerName = parseDealerNameFromText(combinedText);

  if (bac) {
    // Navigate to dealer page
    if (navigate) {
      navigate(`/dealer/${bac}`);
    }
    return {
      type: 'dealer',
      bac,
      dealerName,
      action: 'navigate',
      path: `/dealer/${bac}`,
    };
  }

  if (url) {
    // If URL is shared, try to navigate to it (if same origin)
    try {
      const urlObj = new URL(url, window.location.origin);
      if (urlObj.origin === window.location.origin && navigate) {
        navigate(urlObj.pathname + urlObj.search);
        return {
          type: 'url',
          url,
          action: 'navigate',
          path: urlObj.pathname + urlObj.search,
        };
      }
    } catch {
      // Invalid URL, ignore
    }
  }

  // No actionable content found
  return {
    type: 'text',
    text: combinedText,
    action: 'none',
  };
}

/**
 * Check if app was launched via Web Share Target
 * @returns {Object|null} Shared data if available
 */
export function getSharedData() {
  if (typeof window === 'undefined') return null;

  // Check URL parameters (Web Share Target passes data via URL)
  const urlParams = new URLSearchParams(window.location.search);
  const sharedText = urlParams.get('text');
  const sharedTitle = urlParams.get('title');
  const sharedUrl = urlParams.get('url');

  if (sharedText || sharedTitle || sharedUrl) {
    return {
      text: sharedText || '',
      title: sharedTitle || '',
      url: sharedUrl || '',
    };
  }

  return null;
}


